import type { IAttachmentInfo } from '../../types/api/IAttachmentInfo';
import type { IFacilityInfo } from '../../types/api/IFacilityInfo';
import type { ILateStatus } from '../../types/api/ILateStatus';
import type { IPatientInfo } from '../../types/api/IPatientInfo';
import type { IPractitioner } from '../../types/api/IPractitionerInfo';
import type { IStatusTime } from '../../types/api/IStatusTime';
import type { ITripBase } from '../../types/api/ITrip';
import type { ITripLeg } from '../../types/api/ITripLeg';
import type { IUnitInfo } from '../../types/api/IUnitInfo';
import type { IdValue } from '../../types/api/IdValue';

import { Ref } from '@/decorators/ref';
import { assignOwnProperties } from '@/utils/assignOwnProperties';
import { normalize } from '@/utils/normalize';
import type { ICallerInfo } from '~/api/ICallerInfo';

export class Trip implements ITripBase {
  @Ref appointmentDateTime: string;
  @Ref attachments: IAttachmentInfo[];
  @Ref availableForUnitAssignment: boolean;
  @Ref callDateTime: string;
  @Ref callStartedDateTime: string;
  @Ref callType: IdValue;
  @Ref caller: ICallerInfo;

  @Ref contextKey?: string;
  @Ref correlationKey?: string;
  @Ref destinationId?: string;
  @Ref determinantCode?: string;
  @Ref dispatchComments: string;
  @Ref dispatchDateTime?: string;
  @Ref externalId?: string;
  @Ref governmentId?: string;
  @Ref hostReceivedTime?: string;
  @Ref id: string;
  @Ref isRespond?: string;
  @Ref isStatusChangeOverdue?: string;
  @Ref job: string;
  @Ref lateStatuses?: ILateStatus[];
  @Ref legs: ITripLeg[];
  @Ref name: string;
  @Ref natureOfCall: IdValue;
  @Ref orderingFacility?: IFacilityInfo;
  @Ref orderingPractitioner?: IPractitioner;
  @Ref outOfChute: { assignedTime: string, wheelsRollingTime?: string };
  @Ref ownerId?: string;
  @Ref patient: IPatientInfo;
  @Ref pickupDateTime: string;
  @Ref priority: IdValue;
  @Ref recurringSeriesId?: string;
  @Ref relatedTrips?: unknown[];
  @Ref relatedTripsCount?: number;
  @Ref requestedPickupDateTime?: string;
  @Ref requesterKey?: string;
  @Ref requesterName?: string;
  @Ref responderScript?: string;
  @Ref runNumber: string;
  @Ref sentTime?: string;
  // Shift the trip is pre-assigned to. Must be declared or normalize/assignOwnProperties
  // drops it, leaving pre-assigned trips indistinguishable from the vehicle's active work.
  @Ref shiftId?: string;
  @Ref sourceId?: string;
  @Ref status: IdValue;
  @Ref statusTimes: IStatusTime[];
  @Ref transportPriority?: IdValue;
  @Ref tripNotesCount: number;
  @Ref tripSource?: string;
  @Ref tripStatus: string;
  @Ref unit?: IUnitInfo;
  @Ref unitId: string;
  @Ref userKey?: string;
  @Ref userSecurityToken?: unknown;
  @Ref who?: string;

  constructor(data: Trip) {
    assignOwnProperties(this, normalize(data));
  }
}
